import { useRef, useState, useEffect, useMemo } from 'react'
import { Play, Pause, Download } from 'lucide-react'

// Generate a stable fake waveform from the URL string
function buildBars(src, count = 32) {
  let hash = 0
  for (let i = 0; i < src.length; i++) {
    hash = (hash * 31 + src.charCodeAt(i)) >>> 0
  }
  return Array.from({ length: count }, () => {
    hash = (hash * 1664525 + 1013904223) >>> 0
    const h = 3 + ((hash >>> 16) % 14) // 3–16px
    return h
  })
}

const fmt = (s) => {
  if (!isFinite(s) || s < 0) return '0:00'
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${sec.toString().padStart(2, '0')}`
}

export function VoicePlayer({ src, isDarkMode }) {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [loadError, setLoadError] = useState(false)

  const bars = useMemo(() => buildBars(src || ''), [src])

  // Reset state when src changes
  useEffect(() => {
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setLoadError(false)
  }, [src])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const onTimeUpdate  = () => setCurrentTime(audio.currentTime)
    const onLoaded      = () => { if (isFinite(audio.duration)) setDuration(audio.duration) }
    const onEnded       = () => { setIsPlaying(false); setCurrentTime(0) }
    const onError       = () => setLoadError(true)
    audio.addEventListener('timeupdate',      onTimeUpdate)
    audio.addEventListener('loadedmetadata',  onLoaded)
    audio.addEventListener('durationchange',  onLoaded)
    audio.addEventListener('ended',           onEnded)
    audio.addEventListener('error',           onError)
    return () => {
      audio.removeEventListener('timeupdate',     onTimeUpdate)
      audio.removeEventListener('loadedmetadata', onLoaded)
      audio.removeEventListener('durationchange', onLoaded)
      audio.removeEventListener('ended',          onEnded)
      audio.removeEventListener('error',          onError)
    }
  }, [src])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play().catch(() => setLoadError(true))
      setIsPlaying(true)
    }
  }

  const handleSeek = (e) => {
    const audio = audioRef.current
    if (!audio) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    if (duration) audio.currentTime = ratio * duration
  }

  const progress = duration > 0 ? currentTime / duration : 0

  if (loadError) {
    return (
      <div className={`mt-1 flex items-center justify-between gap-2 px-3 py-2 rounded-2xl border ${
        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'
      }`}>
        <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
          🎙 Voice note (tap to download)
        </span>
        <a href={src} download target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1 px-2 py-0.5 bg-sfp-orange text-white rounded-full text-xs hover:opacity-90 flex-shrink-0">
          <Download className="w-3 h-3" />
        </a>
      </div>
    )
  }

  return (
    <div className={`mt-1 flex items-center gap-2.5 px-3 py-2.5 rounded-2xl border ${
      isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-200'
    }`}>
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Play / Pause */}
      <button
        onClick={togglePlay}
        className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-sfp-orange text-white hover:opacity-90 transition-opacity"
      >
        {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </button>

      {/* Waveform bars + seek */}
      <div
        className="flex-1 flex items-center gap-px cursor-pointer select-none"
        style={{ height: 24 }}
        onClick={handleSeek}
        title="Seek"
      >
        {bars.map((h, i) => {
          const barProgress = i / bars.length
          const filled = barProgress <= progress
          return (
            <div
              key={i}
              className="rounded-full flex-1 transition-colors"
              style={{
                height: h,
                backgroundColor: filled
                  ? '#f97316'
                  : isDarkMode ? '#475569' : '#d1d5db',
              }}
            />
          )
        })}
      </div>

      {/* Time */}
      <span className={`text-xs tabular-nums flex-shrink-0 w-10 text-right ${
        isDarkMode ? 'text-slate-400' : 'text-gray-500'
      }`}>
        {isPlaying || currentTime > 0 ? fmt(currentTime) : fmt(duration)}
      </span>

      {/* Download */}
      <a href={src} download target="_blank" rel="noopener noreferrer"
        className={`flex-shrink-0 hover:text-sfp-orange transition-colors ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}
        title="Download voice note">
        <Download className="w-3.5 h-3.5" />
      </a>
    </div>
  )
}
